/**
 * CORE-D-04 underspending flag on the Client Dashboard.
 *
 * Chans's 2026-09-15 rebuild spec §10 D-04: any tenant flagged as
 * underspending on the M-08 Budget Reallocation tab should also show
 * up on the Client Dashboard, so operators do not have to open a
 * per-tenant tab to see who is falling behind.
 *
 * Phase A signal: FleetTenant.projected_underspend_usd > 0 from the
 * existing /google-ads/weekly/fleet endpoint. This is the same number
 * the M-08 (per-tenant) tab reads as the projected shortfall on the
 * current cycle.
 *
 * Phase B (CORE-D-04.b) will refine the gate to the plan doc's exact
 * acceptance — "< 0.9× ideal_daily for two consecutive ticks" — once
 * the backend surfaces the two-tick persistence. Phase A ships the
 * flag row against the current single-tick projection so operators
 * stop missing underspending clients today.
 *
 * The banner is workspace-agnostic: it lists tenants across the fleet,
 * because the Client Dashboard is the cross-client view.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  listFleet,
  type FleetTenant,
} from '@/services/googleAdsWeeklyService';

interface UnderspendFlagBannerProps {
  /**
   * Called with a tenant's website when the operator clicks through to
   * that client's Budget Reallocation. WeeklyManagementSection wires this
   * to its own tenant-switch handler.
   */
  onDrillInto?: (tenantId: string) => void;
}

/**
 * Any strictly-positive projected underspend for this cycle counts. Phase
 * B tightens the gate; keep the threshold exported so tests + follow-up
 * PRs can share it.
 */
export const UNDERSPEND_THRESHOLD_USD = 0;

function formatUsd(usd: number): string {
  return `$${usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/**
 * Rank the flagged tenants by biggest projected shortfall so the row
 * reads left-to-right most-urgent first.
 */
export function selectUnderspending(
  rows: readonly FleetTenant[],
  threshold: number = UNDERSPEND_THRESHOLD_USD,
): FleetTenant[] {
  return rows
    .filter((r) => (r.latest_run?.projected_underspend_usd ?? 0) > threshold)
    .slice()
    .sort(
      (a, b) =>
        (b.latest_run?.projected_underspend_usd ?? 0)
        - (a.latest_run?.projected_underspend_usd ?? 0),
    );
}

export function UnderspendFlagBanner({ onDrillInto }: UnderspendFlagBannerProps) {
  // Same queryKey as WeeklyFleetBoard so this is a shared cached read —
  // one network trip on the Dashboard, two consumers.
  const q = useQuery({
    queryKey: ['google-ads-weekly-fleet'],
    queryFn: () => listFleet(),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const flagged = useMemo(
    () => selectUnderspending(q.data ?? []),
    [q.data],
  );

  if (q.isLoading || q.isError) {
    // Loading and errors are the fleet board's story; the banner stays
    // out of the way rather than doubling the noise.
    return null;
  }

  if (flagged.length === 0) {
    return null;
  }

  return (
    <div
      role="status"
      aria-label="Underspending clients"
      className="flex flex-wrap items-start gap-3 rounded-md border border-coral/40 bg-coral-light/40 p-4 text-sm dark:border-coral/40 dark:bg-coral-light/20"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-coral-dark" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-coral-dark">
          {flagged.length} client{flagged.length === 1 ? '' : 's'} projected to
          underspend this cycle
        </p>
        <p className="mt-0.5 text-xs text-coral-dark/80">
          Same signal Budget Reallocation flags for each client. Click a name
          to drill into their cycle.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {flagged.map((t) => {
            const usd = t.latest_run?.projected_underspend_usd ?? 0;
            return (
              <Button
                key={t.tenant_id}
                type="button"
                size="sm"
                variant="outline"
                className="border-coral/40 bg-warm-bg text-xs font-medium text-coral-dark hover:bg-coral-light/60"
                onClick={() => onDrillInto?.(t.tenant_id)}
              >
                {t.tenant_id}
                <span className="ml-2 rounded bg-coral-light/60 px-1.5 py-0.5 font-mono text-[10px]">
                  {formatUsd(usd)}
                </span>
                <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default UnderspendFlagBanner;
